// Backlog 073 — pre-upload `.env.example` validation lives client-side. This
// module is the pure-fs / regex layer. The orchestration (talking to the
// server, prompting the user, wiring set-now) lives in env-preflight-flow.ts.
//
// The regex MUST stay aligned with the server's KEY_RE in
// packages/api/src/routes/v2/runtimes-ops.ts (`[-._a-zA-Z][-._a-zA-Z0-9]*`)
// and with packages/api/src/services/tarball-utils.ts:parseDotenvKeys, which
// is the server-side dotenv parser this module mirrors. A narrower client
// parser would yield false-negative warnings.

import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

export interface ServiceEnvKeys {
  service: string;
  keys: string[];
}

/**
 * Parse a `.env.example`-style file body and return the keys declared in it.
 * Mirrors `packages/api/src/services/tarball-utils.ts:parseDotenvKeys`.
 *
 * Rules:
 *  - Comments (`#…`) and blank lines are skipped.
 *  - Whitespace before the key is allowed.
 *  - First occurrence of a duplicate key wins.
 *  - The value half is ignored — we only care about the key list.
 */
export function parseDotenvKeys(content: string): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const raw of content.split(/\r?\n/)) {
    const line = raw.trimStart();
    if (!line || line.startsWith('#')) continue;
    const m = line.match(/^([-._a-zA-Z][-._a-zA-Z0-9]*)\s*=/);
    if (!m) continue;
    const key = m[1];
    if (seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

/**
 * Heuristic classification of an env-var name as `env` (non-sensitive) or
 * `secret` (sensitive). The CLI uses this to suggest the right `cloudgrid
 * env set` vs `grid secrets set` command in the warning's hint.
 *
 * Pattern: any key whose suffix is `_TOKEN`, `_KEY`, `_SECRET`, or
 * `_PASSWORD` (case-insensitive) is classified as a secret. Everything else
 * is env. This is a guess — the user can always pick the other bucket
 * manually. The split exists so we don't suggest `grid env set
 * GITHUB_TOKEN=...` (which works but is the wrong storage).
 */
export function classifyKey(key: string): 'env' | 'secret' {
  return /(_TOKEN|_KEY|_SECRET|_PASSWORD)$/i.test(key) ? 'secret' : 'env';
}

/**
 * For each declared service, walk `services/<svc>/.env.example` (relative
 * to `projectRoot`) and return the set of declared keys. Services without
 * a `.env.example` are silently dropped — the validator is opt-in by the
 * presence of that file (mirrors the server-side validator's policy).
 */
export function collectDeclaredEnvKeys(
  projectRoot: string,
  serviceNames: ReadonlyArray<string>,
): ServiceEnvKeys[] {
  const out: ServiceEnvKeys[] = [];
  for (const service of serviceNames) {
    const path = join(projectRoot, 'services', service, '.env.example');
    if (!existsSync(path)) continue;
    let content: string;
    try {
      content = readFileSync(path, 'utf-8');
    } catch {
      continue;
    }
    const keys = parseDotenvKeys(content);
    if (keys.length > 0) out.push({ service, keys });
  }
  return out;
}
